import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Collapse,
  Form,
  Input,
  message,
  Modal,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  DeleteOutlined,
  HolderOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useFirebaseUpload } from "../../helper/helper";

const { Text } = Typography;

// A grab handle that initiates a drag. `touchAction: none` lets it work on
// touch devices; the dnd-kit listeners/attributes carry the drag behaviour and
// keyboard accessibility.
const DragHandle = ({ attributes, listeners, title = "Drag to reorder" }) => (
  <Tooltip title={title}>
    <Button
      type="text"
      size="small"
      icon={<HolderOutlined />}
      style={{ cursor: "grab", touchAction: "none" }}
      {...attributes}
      {...listeners}
    />
  </Tooltip>
);

// Sortable wrapper for an inspection-area card. Exposes the drag handle's
// listeners/attributes via a render prop so they can live on the panel header
// without making the whole header draggable (which would block expand/collapse).
const SortableArea = ({ id, children }) => {
  const { setNodeRef, transform, transition, isDragging, attributes, listeners } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    marginBottom: 8,
    position: "relative",
    zIndex: isDragging ? 1 : "auto",
  };
  return children({ setNodeRef, style, attributes, listeners });
};

// Up/down/remove cluster (a keyboard/precision fallback to dragging), shared by
// check and step rows.
const RowControls = ({ onUp, onDown, onRemove, upDisabled, downDisabled }) => (
  <Space size={0}>
    <Tooltip title="Move up">
      <Button
        type="text"
        size="small"
        icon={<ArrowUpOutlined />}
        disabled={upDisabled}
        onClick={onUp}
      />
    </Tooltip>
    <Tooltip title="Move down">
      <Button
        type="text"
        size="small"
        icon={<ArrowDownOutlined />}
        disabled={downDisabled}
        onClick={onDown}
      />
    </Tooltip>
    <Tooltip title="Remove">
      <Button
        type="text"
        size="small"
        danger
        icon={<CloseOutlined />}
        onClick={onRemove}
      />
    </Tooltip>
  </Space>
);

// Sortable wrapper for a single check / step row.
const SortableRow = ({ id, children }) => {
  const { setNodeRef, transform, transition, isDragging, attributes, listeners } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    display: "flex",
    gap: 6,
    alignItems: "center",
    marginBottom: 8,
    background: isDragging ? "#fafafa" : "transparent",
  };
  return children({ setNodeRef, style, attributes, listeners });
};

// Shared editor for a list of checklist items (inspection area / question /
// reference image / checks / fix-it steps). Used by both the property
// create/edit modal and the reusable checklist-template modal so the two never
// drift. Must be rendered inside an Ant Design <Form>.
//
// Each area is a collapsible, drag-sortable panel; checks and fix-it steps are
// drag-sortable rows (with up/down buttons as a keyboard/precision fallback).
// All mutations are immutable updates against `setQuestionaire`. The saved item
// shape (id / area / question / image / checks[] / resolutionSteps[]) is
// produced by both parents' normalizers, so the transient drag ids we attach
// here (`_id` on each check, `_stepIds` parallel to resolutionSteps) are
// stripped on save. The legacy handleAdd*/handleRemove* props are accepted for
// backwards-compat but no longer used — this component owns its own logic.
const ChecklistItemsEditor = ({ questionaire, setQuestionaire }) => {
  const [activeKeys, setActiveKeys] = useState([]);
  const [pendingFocus, setPendingFocus] = useState(null);
  const { uploadFiles } = useFirebaseUpload();
  const fieldRefs = useRef({});
  const idCounter = useRef(0);

  const nextId = () => `c${Date.now()}-${idCounter.current++}`;

  const sensors = useSensors(
    // A small drag threshold so a plain click on the handle (or the nearby
    // up/down buttons) doesn't start a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // Focus a freshly-added input once it's in the DOM, so adding a row and
  // typing is a single motion rather than add-then-click.
  useEffect(() => {
    if (pendingFocus && fieldRefs.current[pendingFocus]) {
      fieldRefs.current[pendingFocus].focus();
      setPendingFocus(null);
    }
  }, [pendingFocus, questionaire]);

  const registerRef = (key) => (node) => {
    if (node) fieldRefs.current[key] = node;
    else delete fieldRefs.current[key];
  };

  const imageOnlyBeforeUpload = (file) => {
    const isImage = file?.type?.startsWith("image/");
    if (!isImage) {
      message.error("Only image files are allowed");
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const newItem = (id) => ({
    id,
    area: "",
    question: "",
    image: null,
    note: "",
    checks: [{ label: "", _id: nextId() }],
    resolutionSteps: [""],
    _stepIds: [nextId()],
  });

  // Backfill stable drag ids on items that arrived without them (existing
  // templates, applied templates, property edits). Done in an effect — not by
  // mutating state during render — so render stays pure. Idempotent: only sets
  // when something is missing, so it no-ops (no loop) once ids are present.
  useEffect(() => {
    let changed = false;
    const next = questionaire.map((q) => {
      let checks = q.checks || [];
      if (checks.some((p) => !p._id)) {
        checks = checks.map((p) => (p._id ? p : { ...p, _id: nextId() }));
        changed = true;
      }
      const steps = q.resolutionSteps || [];
      let stepIds = q._stepIds;
      if (!stepIds || stepIds.length !== steps.length) {
        const existing = stepIds || [];
        stepIds = steps.map((_, i) => existing[i] || nextId());
        changed = true;
      }
      if (checks === q.checks && stepIds === q._stepIds) return q;
      return { ...q, checks, _stepIds: stepIds };
    });
    if (changed) setQuestionaire(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionaire]);

  // Stable id for a check/step row in render. After the backfill effect runs
  // these are the real `_id`/`_stepIds`; on the very first render of freshly
  // loaded data they fall back to a deterministic id so dnd-kit and React keys
  // never see `undefined` (a drag can't happen in that sub-frame anyway).
  const checkId = (q, p, i) => p._id || `c-${q.id}-${i}`;
  const stepId = (q, i) => (q._stepIds && q._stepIds[i]) || `s-${q.id}-${i}`;

  const toggleArea = (id, open) =>
    setActiveKeys((prev) =>
      open ? [...new Set([...prev, id])] : prev.filter((k) => k !== id),
    );

  // ---- Area operations -----------------------------------------------------
  const addArea = () => {
    const id = nextId();
    setQuestionaire((prev) => [...prev, newItem(id)]);
    setActiveKeys((prev) => [...prev, id]);
    setPendingFocus(`area-area-${id}`);
  };

  const removeArea = (qIndex) => {
    const it = questionaire[qIndex];
    const hasContent =
      it.area ||
      it.question ||
      it.image ||
      (it.checks || []).some((p) => p.label) ||
      (it.resolutionSteps || []).some((s) => s);
    const doRemove = () =>
      setQuestionaire((prev) => prev.filter((_, i) => i !== qIndex));
    if (hasContent) {
      Modal.confirm({
        title: "Remove this inspection area?",
        content: "Its checks and fix-it steps will be deleted from the checklist.",
        okText: "Remove",
        okButtonProps: { danger: true },
        onOk: doRemove,
      });
    } else {
      doRemove();
    }
  };

  const moveArea = (qIndex, dir) =>
    setQuestionaire((prev) => {
      const j = qIndex + dir;
      if (j < 0 || j >= prev.length) return prev;
      return arrayMove(prev, qIndex, j);
    });

  const patchArea = (qIndex, key, value) =>
    setQuestionaire((prev) =>
      prev.map((it, i) => (i === qIndex ? { ...it, [key]: value } : it)),
    );

  const setAreaImage = (qIndex, file) => {
    // Do the blob-URL work HERE (event handler), not inside the state updater —
    // updaters must be pure (React may invoke them more than once), and
    // create/revoke are side effects.
    const area = questionaire[qIndex];
    const areaId = area?.id;
    const prevImage = area?.image;
    if (prevImage && prevImage.startsWith("blob:")) {
      URL.revokeObjectURL(prevImage);
    }

    // Cleared.
    if (!file) {
      setQuestionaire((prev) =>
        prev.map((it) =>
          it.id === areaId ? { ...it, image: null, imageFile: null } : it,
        ),
      );
      return;
    }

    // Show an instant local preview and keep the File as a save-time fallback…
    const preview = URL.createObjectURL(file);
    setQuestionaire((prev) =>
      prev.map((it) =>
        it.id === areaId ? { ...it, image: preview, imageFile: file } : it,
      ),
    );

    // …then upload right away so the item carries a real, draftable URL and the
    // save is instant. Match by area id (rows can reorder mid-upload) and only
    // if this same File is still the one selected.
    uploadFiles([file])
      .then((uploaded) => {
        const url = uploaded?.[0]?.image;
        if (!url) throw new Error("no url");
        setQuestionaire((prev) =>
          prev.map((it) =>
            it.id === areaId && it.imageFile === file
              ? { ...it, image: url, imageFile: null }
              : it,
          ),
        );
        if (preview.startsWith("blob:")) URL.revokeObjectURL(preview);
      })
      .catch(() => {
        // Keep the preview + File so onFinish uploads it as a fallback.
        message.error("Photo upload failed — we'll retry when you save.");
      });
  };

  const onAreaDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setQuestionaire((prev) => {
      const from = prev.findIndex((q) => q.id === active.id);
      const to = prev.findIndex((q) => q.id === over.id);
      if (from < 0 || to < 0) return prev;
      return arrayMove(prev, from, to);
    });
  };

  // ---- Check ("things to check") operations --------------------------------
  const addCheck = (qIndex, areaId) => {
    const len = questionaire[qIndex]?.checks?.length || 0;
    setQuestionaire((prev) =>
      prev.map((it, i) =>
        i === qIndex
          ? {
              ...it,
              checks: [
                ...it.checks,
                { label: "", _id: nextId() },
              ],
            }
          : it,
      ),
    );
    setPendingFocus(`check-${areaId}-${len}`);
  };

  const patchCheck = (qIndex, pIndex, value) =>
    setQuestionaire((prev) =>
      prev.map((it, i) =>
        i === qIndex
          ? {
              ...it,
              checks: it.checks.map((p, pi) =>
                pi === pIndex ? { ...p, label: value } : p,
              ),
            }
          : it,
      ),
    );

  const removeCheck = (qIndex, pIndex) =>
    setQuestionaire((prev) =>
      prev.map((it, i) =>
        i === qIndex
          ? { ...it, checks: it.checks.filter((_, pi) => pi !== pIndex) }
          : it,
      ),
    );

  const moveCheck = (qIndex, pIndex, dir) =>
    setQuestionaire((prev) =>
      prev.map((it, i) => {
        if (i !== qIndex) return it;
        const j = pIndex + dir;
        if (j < 0 || j >= it.checks.length) return it;
        return { ...it, checks: arrayMove(it.checks, pIndex, j) };
      }),
    );

  const onCheckDragEnd = (qIndex, { active, over }) => {
    if (!over || active.id === over.id) return;
    setQuestionaire((prev) =>
      prev.map((it, i) => {
        if (i !== qIndex) return it;
        const from = it.checks.findIndex((p) => p._id === active.id);
        const to = it.checks.findIndex((p) => p._id === over.id);
        if (from < 0 || to < 0) return it;
        return { ...it, checks: arrayMove(it.checks, from, to) };
      }),
    );
  };

  // ---- Fix-it step operations ----------------------------------------------
  const addStep = (qIndex, areaId) => {
    const len = questionaire[qIndex]?.resolutionSteps?.length || 0;
    setQuestionaire((prev) =>
      prev.map((it, i) =>
        i === qIndex
          ? {
              ...it,
              resolutionSteps: [...(it.resolutionSteps || []), ""],
              _stepIds: [...(it._stepIds || []), nextId()],
            }
          : it,
      ),
    );
    setPendingFocus(`step-${areaId}-${len}`);
  };

  const patchStep = (qIndex, sIndex, value) =>
    setQuestionaire((prev) =>
      prev.map((it, i) =>
        i === qIndex
          ? {
              ...it,
              resolutionSteps: (it.resolutionSteps || []).map((s, si) =>
                si === sIndex ? value : s,
              ),
            }
          : it,
      ),
    );

  const removeStep = (qIndex, sIndex) =>
    setQuestionaire((prev) =>
      prev.map((it, i) =>
        i === qIndex
          ? {
              ...it,
              resolutionSteps: (it.resolutionSteps || []).filter(
                (_, si) => si !== sIndex,
              ),
              _stepIds: (it._stepIds || []).filter((_, si) => si !== sIndex),
            }
          : it,
      ),
    );

  const moveStep = (qIndex, sIndex, dir) =>
    setQuestionaire((prev) =>
      prev.map((it, i) => {
        if (i !== qIndex) return it;
        const steps = it.resolutionSteps || [];
        const j = sIndex + dir;
        if (j < 0 || j >= steps.length) return it;
        return {
          ...it,
          resolutionSteps: arrayMove(steps, sIndex, j),
          _stepIds: arrayMove(it._stepIds || [], sIndex, j),
        };
      }),
    );

  const onStepDragEnd = (qIndex, { active, over }) => {
    if (!over || active.id === over.id) return;
    setQuestionaire((prev) =>
      prev.map((it, i) => {
        if (i !== qIndex) return it;
        const ids = it._stepIds || [];
        const from = ids.findIndex((id) => id === active.id);
        const to = ids.findIndex((id) => id === over.id);
        if (from < 0 || to < 0) return it;
        return {
          ...it,
          resolutionSteps: arrayMove(it.resolutionSteps || [], from, to),
          _stepIds: arrayMove(ids, from, to),
        };
      }),
    );
  };

  const panelBody = (q, qIndex) => {
    const started = Boolean(
      q.area ||
        q.question ||
        (q.checks || []).some((p) => p.label) ||
        (q.resolutionSteps || []).some((s) => s),
    );
    const checks = q.checks || [];
    const steps = q.resolutionSteps || [];

    return (
      <>
        <Form.Item
          label="Inspection Area"
          required
          validateStatus={started && !q.area ? "error" : ""}
          help={started && !q.area ? "Required" : undefined}
          style={{ marginBottom: 12 }}
        >
          <Input
            ref={registerRef(`area-area-${q.id}`)}
            placeholder="e.g. Kitchen, Master Bathroom, Roof"
            value={q.area}
            onChange={(e) => patchArea(qIndex, "area", e.target.value)}
          />
        </Form.Item>

        <Form.Item
          label="Area prompt (optional)"
          help="Shown to the inspector above the checks for this area."
          style={{ marginBottom: 12 }}
        >
          <Input
            ref={registerRef(`area-q-${q.id}`)}
            placeholder="e.g. Walk the kitchen and check each item below"
            value={q.question}
            onChange={(e) => patchArea(qIndex, "question", e.target.value)}
          />
        </Form.Item>

        <Form.Item label="Reference photo (optional)" style={{ marginBottom: 12 }}>
          <Upload
            listType="picture-card"
            accept="image/*"
            fileList={
              q.image
                ? [
                    {
                      uid: q.id || `question-${qIndex}`,
                      name: "question-image",
                      status: "done",
                      url: q.image,
                      thumbUrl: q.image,
                    },
                  ]
                : []
            }
            beforeUpload={imageOnlyBeforeUpload}
            onChange={({ fileList: newFileList }) => {
              if (newFileList.length > 0) {
                const f = newFileList[0];
                if (f?.originFileObj) setAreaImage(qIndex, f.originFileObj);
              } else {
                setAreaImage(qIndex, null);
              }
            }}
          >
            {!q.image && (
              <div>
                <UploadOutlined />
                <div>Upload</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <Text strong style={{ display: "block", marginBottom: 2 }}>
          Things to check <Text type="danger">*</Text>
        </Text>
        <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 12 }}>
          These are the Yes/No questions the inspector answers — a “No” requires a
          photo. Add at least one.
        </Text>
        {checks.length === 0 && (
          <Text
            type={started ? "danger" : "secondary"}
            style={{ display: "block", marginBottom: 8 }}
          >
            {started ? "Add at least one check" : "No checks yet."}
          </Text>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => onCheckDragEnd(qIndex, e)}
        >
          <SortableContext
            items={checks.map((p, i) => checkId(q, p, i))}
            strategy={verticalListSortingStrategy}
          >
            {checks.map((p, pIndex) => (
              <SortableRow key={checkId(q, p, pIndex)} id={checkId(q, p, pIndex)}>
                {({ setNodeRef, style, attributes, listeners }) => (
                  <div ref={setNodeRef} style={style}>
                    <DragHandle
                      attributes={attributes}
                      listeners={listeners}
                      title="Drag to reorder check"
                    />
                    <Input
                      ref={registerRef(`check-${q.id}-${pIndex}`)}
                      placeholder="e.g. No leaks under sink"
                      value={p.label}
                      onChange={(e) => patchCheck(qIndex, pIndex, e.target.value)}
                    />
                    <RowControls
                      onUp={() => moveCheck(qIndex, pIndex, -1)}
                      onDown={() => moveCheck(qIndex, pIndex, 1)}
                      onRemove={() => removeCheck(qIndex, pIndex)}
                      upDisabled={pIndex === 0}
                      downDisabled={pIndex === checks.length - 1}
                    />
                  </div>
                )}
              </SortableRow>
            ))}
          </SortableContext>
        </DndContext>
        <Button
          onClick={() => addCheck(qIndex, q.id)}
          icon={<PlusOutlined />}
          type="dashed"
          block
          style={{ marginBottom: 20 }}
        >
          Add check
        </Button>

        <Text strong style={{ display: "block", marginBottom: 8 }}>
          If there&apos;s a problem, do this{" "}
          <Text type="secondary" style={{ fontWeight: 400 }}>
            (optional)
          </Text>
        </Text>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => onStepDragEnd(qIndex, e)}
        >
          <SortableContext
            items={steps.map((_, i) => stepId(q, i))}
            strategy={verticalListSortingStrategy}
          >
            {steps.map((step, sIndex) => (
              <SortableRow key={stepId(q, sIndex)} id={stepId(q, sIndex)}>
                {({ setNodeRef, style, attributes, listeners }) => (
                  <div ref={setNodeRef} style={style}>
                    <DragHandle
                      attributes={attributes}
                      listeners={listeners}
                      title="Drag to reorder step"
                    />
                    <Text type="secondary" style={{ width: 18, textAlign: "right" }}>
                      {sIndex + 1}.
                    </Text>
                    <Input
                      ref={registerRef(`step-${q.id}-${sIndex}`)}
                      placeholder="e.g. Check the water shut-off valve under the sink"
                      value={step}
                      onChange={(e) => patchStep(qIndex, sIndex, e.target.value)}
                    />
                    <RowControls
                      onUp={() => moveStep(qIndex, sIndex, -1)}
                      onDown={() => moveStep(qIndex, sIndex, 1)}
                      onRemove={() => removeStep(qIndex, sIndex)}
                      upDisabled={sIndex === 0}
                      downDisabled={sIndex === steps.length - 1}
                    />
                  </div>
                )}
              </SortableRow>
            ))}
          </SortableContext>
        </DndContext>
        <Button
          onClick={() => addStep(qIndex, q.id)}
          icon={<PlusOutlined />}
          type="dashed"
          block
        >
          Add step
        </Button>
      </>
    );
  };

  const areaLabel = (q, qIndex) => {
    const checkCount = (q.checks || []).filter((p) => p.label?.trim()).length;
    const stepCount = (q.resolutionSteps || []).filter((s) => s?.trim()).length;
    const incomplete = !q.area?.trim() || checkCount === 0;
    return (
      <Space size={8} wrap>
        <Text type="secondary">#{qIndex + 1}</Text>
        <Text strong>{q.area?.trim() || "Untitled area"}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {checkCount} {checkCount === 1 ? "check" : "checks"} · {stepCount}{" "}
          {stepCount === 1 ? "step" : "steps"}
        </Text>
        {incomplete && <Tag color="warning">Incomplete</Tag>}
      </Space>
    );
  };

  const areaExtra = (q, qIndex, attributes, listeners) => (
    <Space size={0} onClick={(e) => e.stopPropagation()}>
      <DragHandle
        attributes={attributes}
        listeners={listeners}
        title="Drag to reorder area"
      />
      <Tooltip title="Move area up">
        <Button
          type="text"
          size="small"
          icon={<ArrowUpOutlined />}
          disabled={qIndex === 0}
          onClick={() => moveArea(qIndex, -1)}
        />
      </Tooltip>
      <Tooltip title="Move area down">
        <Button
          type="text"
          size="small"
          icon={<ArrowDownOutlined />}
          disabled={qIndex === questionaire.length - 1}
          onClick={() => moveArea(qIndex, 1)}
        />
      </Tooltip>
      <Tooltip title="Remove area">
        <Button
          type="text"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeArea(qIndex)}
        />
      </Tooltip>
    </Space>
  );

  if (questionaire.length === 0) {
    return (
      <div
        style={{
          border: "1px dashed #d9d2c7",
          borderRadius: 8,
          padding: "28px 16px",
          textAlign: "center",
          marginBottom: 12,
        }}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
          No inspection areas yet. Start by adding one (e.g. Kitchen, Bathroom,
          Roof).
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={addArea}>
          Add inspection area
        </Button>
      </div>
    );
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onAreaDragEnd}
      >
        <SortableContext
          items={questionaire.map((q) => q.id)}
          strategy={verticalListSortingStrategy}
        >
          {questionaire.map((q, qIndex) => (
            <SortableArea key={q.id} id={q.id}>
              {({ setNodeRef, style, attributes, listeners }) => (
                <div ref={setNodeRef} style={style}>
                  <Collapse
                    activeKey={activeKeys.includes(q.id) ? [q.id] : []}
                    onChange={(keys) => toggleArea(q.id, keys.length > 0)}
                    items={[
                      {
                        key: q.id,
                        label: areaLabel(q, qIndex),
                        extra: areaExtra(q, qIndex, attributes, listeners),
                        children: panelBody(q, qIndex),
                      },
                    ]}
                  />
                </div>
              )}
            </SortableArea>
          ))}
        </SortableContext>
      </DndContext>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addArea}
        block
        style={{ marginTop: 4 }}
      >
        Add inspection area
      </Button>
    </>
  );
};

export default ChecklistItemsEditor;
